import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, MessageSquare, Palette, Layers, SquarePen } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { useNavigate } from 'react-router-dom';

import { logger } from '@/utils/logger';
interface ChatSession {
  name: string;
  session_title: string;
  category?: string;
  started_at?: string;
  modified?: string;
}

// Dynamic grouped sessions - keys are date strings like "Today", "Yesterday", "2026-01-02"
type GroupedSessions = Record<string, ChatSession[]>;

interface ChatSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChatSearchModal: React.FC<ChatSearchModalProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [groupedSessions, setGroupedSessions] = useState<GroupedSessions | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      // Only fetch if we don't have data yet
      if (!groupedSessions) {
        fetchGroupedSessions();
      }
    }
  }, [open]);

  // Reset state when modal closes (with delay to avoid flash)
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setSearchQuery('');
        setGroupedSessions(null);
        setHasSearched(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Debounced search - only when query changes and has 3+ characters
  useEffect(() => {
    if (!open) return;

    // If query is empty, fetch initial grouped sessions
    if (searchQuery === '') {
      fetchGroupedSessions();
      return;
    }

    // Only search when 3+ characters
    if (searchQuery.length < 3) return;

    const debounceTimer = setTimeout(() => {
      searchChatSessions(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Fetch grouped sessions by date (for initial load / empty query)
  const fetchGroupedSessions = useCallback(async () => {
    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.post(
        getApiEndpoint('GET_GROUPED_CHAT_SESSIONS_BY_DATE'),
        { search_query: '' },
        { withCredentials: true, timeout: 30000 }
      );

      const data = response.data?.message;
      // Handle response structure: message.data.grouped_sessions
      if (data?.success && data?.data?.grouped_sessions) {
        setGroupedSessions(data.data.grouped_sessions);
      } else if (data?.data?.grouped_sessions) {
        setGroupedSessions(data.data.grouped_sessions);
      } else if (data?.grouped_sessions) {
        setGroupedSessions(data.grouped_sessions);
      } else {
        setGroupedSessions({});
      }
    } catch (error) {
      logger.error('Error fetching grouped sessions:', error);
      setGroupedSessions({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Search chat sessions (when query has 3+ characters)
  const searchChatSessions = useCallback(async (query: string) => {
    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.post(
        getApiEndpoint('SEARCH_DESIGN_CHAT_SESSIONS'),
        { search_query: query },
        { withCredentials: true, timeout: 30000 }
      );

      const data = response.data?.message;
      // Handle search response - convert to grouped format
      if (data?.success && data?.data?.grouped_sessions) {
        setGroupedSessions(data.data.grouped_sessions);
      } else if (data?.data?.sessions) {
        // If search returns flat sessions, group them under "Search Results"
        setGroupedSessions({ 'Search Results': data.data.sessions });
      } else if (data?.sessions) {
        setGroupedSessions({ 'Search Results': data.sessions });
      } else if (data?.data?.grouped_sessions) {
        setGroupedSessions(data.data.grouped_sessions);
      } else if (data?.grouped_sessions) {
        setGroupedSessions(data.grouped_sessions);
      } else {
        setGroupedSessions({});
      }
    } catch (error) {
      logger.error('Error searching chat sessions:', error);
      setGroupedSessions({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // All chats route to the single /dashboard workspace now
  const getChatRoute = (_category?: string): string => '/dashboard';

  const getCategoryIcon = (_category?: string) => (
    <MessageSquare className="h-3.5 w-3.5" />
  );

  // Handle opening a chat session
  const handleOpenSession = (session: ChatSession) => {
    onOpenChange(false);
    const route = getChatRoute(session.category);
    navigate(`${route}?session_id=${session.name}`);
  };

  // Check if there are any results
  const hasResults = groupedSessions && Object.values(groupedSessions).some(
    (sessions) => sessions && Array.isArray(sessions) && sessions.length > 0
  );

  // Get ordered date keys (Today, Yesterday first, then dates in descending order)
  const getOrderedDateKeys = (): string[] => {
    if (!groupedSessions) return [];

    // Filter out keys with empty or undefined arrays
    const keys = Object.keys(groupedSessions).filter(
      (key) => groupedSessions[key] && Array.isArray(groupedSessions[key]) && groupedSessions[key].length > 0
    );
    const priorityOrder = ['Today', 'Yesterday'];

    return keys.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);

      // If both are in priority order, sort by priority
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      // Priority items come first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // For dates, sort descending (newest first)
      return b.localeCompare(a);
    });
  };

  // Format date key for display (convert YYYY-MM-DD to readable format)
  const formatDateKey = (dateKey: string): string => {
    if (dateKey === 'Today' || dateKey === 'Yesterday') return dateKey;

    // Try to parse as date (YYYY-MM-DD)
    const date = new Date(dateKey);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });
    }
    return dateKey;
  };

  // Format relative date from session's started_at
  const formatRelativeDate = (dateString?: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sessionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffTime = today.getTime() - sessionDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  // Skeleton loader for search results
  const renderSkeleton = () => (
    <div className="p-2 space-y-4">
      {/* First group skeleton */}
      <div>
        <Skeleton className="h-3 w-16 mb-3 ml-3 bg-muted" />
        <div className="space-y-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5">
              <Skeleton className="h-4 w-4 rounded bg-muted" />
              <Skeleton className="h-4 flex-1 bg-muted" />
            </div>
          ))}
        </div>
      </div>
      {/* Second group skeleton */}
      <div>
        <Skeleton className="h-3 w-24 mb-3 ml-3 bg-muted" />
        <div className="space-y-1">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5">
              <Skeleton className="h-4 w-4 rounded bg-muted" />
              <Skeleton className="h-4 flex-1 bg-muted" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render a group of sessions
  const renderSessionGroup = (dateKey: string, sessions: ChatSession[]) => {
    if (!sessions || !Array.isArray(sessions) || sessions.length === 0) return null;

    return (
      <div key={dateKey} className="mb-4">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
          {formatDateKey(dateKey)}
        </h3>
        <div className="space-y-0.5">
          {sessions.map((session) => (
            <button
              key={session.name}
              onClick={() => handleOpenSession(session)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted rounded-lg transition-colors focus:outline-none group"
            >
              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                {getCategoryIcon(session.category)}
              </span>
              <span className="flex-1 text-sm text-foreground truncate">
                {session.session_title || session.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark sm:max-w-[700px] p-0 gap-0 bg-background text-foreground border-border duration-150">
        {/* Visually hidden title for accessibility */}
        <VisuallyHidden.Root>
          <DialogTitle>Search Chats</DialogTitle>
        </VisuallyHidden.Root>

        {/* Search Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-border">
          <Search className="h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-muted-foreground hover:text-foreground focus:outline-none"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* New Chat Button */}
        <button
          onClick={() => {
            onOpenChange(false);
            navigate('/dashboard');
          }}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors focus:outline-none"
        >
          <SquarePen className="h-5 w-5 text-foreground" />
          <span className="text-sm font-medium text-foreground">New Chat</span>
        </button>

        {/* Results */}
        <ScrollArea className="h-[400px] [&>div>div]:!block [&_[data-radix-scroll-area-scrollbar]]:hidden">
          {isLoading ? (
            renderSkeleton()
          ) : (
          <div className="p-2">
            {hasResults ? (
              <>
                {getOrderedDateKeys().map((dateKey) =>
                  renderSessionGroup(dateKey, groupedSessions![dateKey])
                )}
              </>
            ) : hasSearched ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MessageSquare className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? `No chats found for "${searchQuery}"` : 'No chats yet'}
                </p>
              </div>
            ) : null}
          </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
